import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  ILocation,
  IWarehouseLocation,
  IWarehouseLocationImportRequest,
  IWarehouseLocationImportResponse,
  IWarehouseLocationsResponse,
} from "../models/Location";

export default function useWarehouseLocations() {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const warehouseLocations = useQuery({
    queryKey: ["warehouseLocations"],
    queryFn: () => get<IWarehouseLocationsResponse>("/warehouse/locations"),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const create = useMutation(
    (location: IWarehouseLocation) => post("/warehouse/locations", location),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["warehouseLocations"]);
      },
    }
  );

  const update = useMutation(
    (location: IWarehouseLocation) => put(`/warehouse/locations/${location.id}`, location),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["warehouseLocations"]);
      },
    }
  );

  const remove = useMutation(
    (locationId: string) => del(`/warehouse/locations/${locationId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["warehouseLocations"]);
      },
    }
  );

  // Bulk create from a pasted list. The same call previews and commits - previewOnly decides -
  // so what an admin approves on screen is worked out by the code that does the writing.
  const importLocations = useMutation(
    (request: IWarehouseLocationImportRequest) =>
      post<IWarehouseLocationImportRequest, IWarehouseLocationImportResponse>(
        "/warehouse/locations/import",
        request
      ),
    {
      onSuccess: (_data, request) => {
        // A preview writes nothing, so the cached list is still good.
        if (!request.previewOnly) {
          queryClient.invalidateQueries(["warehouseLocations"]);
        }
      },
    }
  );

  return {
    warehouseLocations,
    create,
    update,
    remove,
    importLocations,
  };
}
